// app/src/features/settings/useSettingsWiring.js
import { useEffect, useRef, useState } from "react";
import { useAlternateSpaceDefault } from "../../hooks/useAlternateSpaceDefault.js";
import { useDisplayScale } from "../../hooks/useDisplayScale.js";
import { useEngineSelection } from "../../hooks/useEngineSelection.js";
import { useIsPhone } from "../../hooks/useIsPhone.js";
import { usePersistedState, JSON_CODEC } from "../../hooks/usePersistedState.js";
import { usePreprocessVariant } from "../../hooks/usePreprocessVariant.js";
import { useResponseCaptureOptIn } from "../../hooks/useResponseCaptureOptIn.js";
import { useActiveForecast } from "../../hooks/useActiveForecast.js";
import { useSession } from "../../hooks/useSession.js";
import { useTheme } from "../../hooks/useTheme.js";
import { themeColors } from "../../lib/colors.js";
import { rootZoomFor } from "../../lib/displayScale.js";
import { ALTERNATE_SPACE_DEFAULT_KEY } from "../../lib/forecast/alternateSpaces.js";
import { captureChatTarget, resolveChatTarget } from "../../lib/forecast/chatTarget.js";
import { GLOW_DEFAULTS } from "../../lib/timeline/glowSpineModel.js";
import { getPreprocessVariants } from "../../services/forecastApi.js";

/**
 * The settings wiring: the persisted preferences and the state the Settings and Account popovers
 * read or set — the alternate-space default (its pref, the derived gate and the hook that applies
 * it to a run), the client-side debug toggle with the response-capture opt-in, the two popovers'
 * open flags with the outside-click close, the preprocessor-variant picker, the engine selection,
 * the theme / display scale / phone reads (and the root zoom and raw palette derived from them)
 * and the voice state with its auto-read effect. Called where `AppInner` called `useTheme`
 * (after the report wiring, before the series wiring, which picks the theme's slot colors); the
 * other cells register here now, in their old relative order (the PR's hook-order note).
 *
 * Every `useState` setter is returned raw (never wrapped). `setVoiceMode` is deliberately not
 * returned: Voice Mode's toggle is removed for now (the directive below), the state stays wired so
 * it can be restored without re-plumbing. `debugUIToggle` and `prevMsgCountRef` are internal.
 *
 * Not here: `lastObs` / `lastBugObs` (the bug report's and four wirings' inputs), the two
 * popovers' context literals (`AppInner`'s — they read other rows' bindings declared after this
 * point), `pendingEngineRevertRef` (submit's and chat's) and `redBlueTeam` (the timeline's).
 * `alternateSpacesEnabled`, `forecastTracking` (`_forecastTrackingAllowed`), `_debugUIAllowed`, `me`
 * and `preprocessVariantDefault` are read from the session context through `useSession()`.
 *
 * @param {object} deps
 * @param {object|null} deps.viewSnap  the reforecast scrub's snapshot
 * @param {object|null} deps.viewEntry  the reforecast scrub's entry
 * @param {object} deps.runState  the run store's render-time state (injected, never imported — C-23)
 */
export function useSettingsWiring({ viewSnap, viewEntry, runState }) {
  const {
    alternateSpacesEnabled,
    _forecastTrackingAllowed: forecastTracking,
    _debugUIAllowed,
    debugMenuAllowed,
    me,
    preprocessVariantDefault,
  } = useSession();
  const active = useActiveForecast();
  const [alternateSpaceDefaultPref, setAlternateSpaceDefaultPref] = usePersistedState(
    ALTERNATE_SPACE_DEFAULT_KEY,
    false,
  );
  const alternateSpaceDefault = alternateSpacesEnabled && alternateSpaceDefaultPref;
  const { applyToRun: applyAlternateSpace } = useAlternateSpaceDefault({
    enabled: alternateSpaceDefault && forecastTracking,
    active,
    viewSnap,
    viewEntry,
  });
  // Client-side debug toggle (hidden triple-click on the Settings title), persisted
  // per-browser (`sooth-debugUI`), and ANDed with the server's `debugMenuEnabled` (/api/me):
  // wherever the deployment exposes the debug UI, plus a verified staff account. What calls a
  // debug route ANDs `debugUIEnabled` again (the variant picker below, the simulate buttons).
  const [debugUIToggle, setDebugUI] = usePersistedState("sooth-debugUI", false);
  const debugUI = debugUIToggle && debugMenuAllowed === true;
  const { responseCaptureOptIn, setResponseCaptureOptIn } = useResponseCaptureOptIn({
    sub: me?.sub,
    email: me?.email,
  });
  // Triple-click on the SETTINGS header toggles the (hidden) Debug Mode.
  // Tracks click count + timestamp of the previous click; resets when the
  // gap exceeds 700ms or once a triple fires.
  const settingsTitleClickRef = useRef({ count: 0, t: 0 });
  const [proOpen, setProOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  useEffect(() => {
    if (!proOpen && !accountOpen) return;
    const onDoc = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (
        t.closest(
          "[data-settings-panel],[data-settings-toggle],[data-account-panel],[data-account-toggle]",
        )
      )
        return;
      if (proOpen) setProOpen(false);
      if (accountOpen) setAccountOpen(false);
    };
    // telemetry-gesture: an outside click closes the settings or account popover (dismissing)
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [proOpen, accountOpen]);
  const [proColors] = usePersistedState("sooth-proColors", false);
  // ── Preprocessor variant picker (debug-only) — the ONE debug affordance that is
  // deliberately NOT available on every deployment. Unlike the view-layer forcings above
  // it changes what the pipeline RUNS, so it is ANDed with the server's own
  // `debugUIEnabled` (EXPOSE_DEBUG_UI + the `chatboard-debug-ui` flag), which prod can
  // never pass. The server re-decides per request regardless — see the hook's header. All
  // of its state lives in the hook, so this shell gains a hook CALL and no `useState`.
  const {
    available: preprocessVariantAvailable,
    variantId: preprocessVariantId,
    setVariantId: setPreprocessVariantId,
    variants: preprocessVariants,
    deploymentDefault: preprocessVariantDeploymentDefault,
    loading: preprocessVariantsLoading,
  } = usePreprocessVariant({
    debugUIEnabled: _debugUIAllowed,
    debugUI,
    deploymentDefault: preprocessVariantDefault,
    fetchVariants: getPreprocessVariants,
  });
  // Experimental "glow spine" timeline rendering (debug-only): when on, the SVG GlowSpine replaces
  // the dashed-CSS spine in ForecastLane. `glowControls` holds the live-tuning slider values,
  // persisted so a tuning survives reload.
  const [glowSpine, setGlowSpine] = usePersistedState("sooth-glowSpine", false);
  const [glowControls, setGlowControls] = usePersistedState(
    "sooth-glowSpine-controls",
    GLOW_DEFAULTS,
    JSON_CODEC,
  );
  const {
    compareEngine,
    setCompareEngine,
    forecastEngine,
    setForecastEngine,
    mirabilisLanes,
    setMirabilisLanes,
    orchestrators,
    setOrchestrators,
    triggerLlm,
    setTriggerLlm,
    compareTriggerLlm,
    setCompareTriggerLlm,
    healthError,
    retryHealth,
  } = useEngineSelection();
  const [theme, setTheme] = useTheme();
  const [uiScaleSetting, setUiScale, uiScale] = useDisplayScale();
  // Also used to un-zoom viewport (clientX/Y) coordinates for fixed-position overlays (tooltips)
  // that render inside the zoomed frame.
  // A phone renders the read-only layout at 1:1; the display scale is desktop-only.
  const isPhone = useIsPhone();
  const uiZoom = isPhone ? 1 : rootZoomFor(uiScale);
  // Raw-hex palette for the current theme. `C` holds `var(--c-*)` strings, but SVG presentation
  // attributes (`fill`/`stroke`/`stopColor`) don't resolve `var()`, `${color}44` hex-alpha can't be
  // composed from a `var()`, and `color === C.blueTeam` identity checks need one representation. So
  // the timeline's SVG render helpers, the colors they feed downstream (LANE_COLORS, the lane/pie
  // colors handed to ForecastLane/GlowSpine), and the discrete hex-alpha style values all read
  // `themeRaw` instead.
  const themeRaw = themeColors(theme);
  // eslint-disable-next-line no-unused-vars -- Voice Mode's toggle is temporarily removed per request (see SettingsModal); the state + setter stay wired so it can be restored without re-plumbing.
  const [voiceMode, setVoiceMode] = useState(false);
  const [autoRead, setAutoRead] = useState(false);
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const recognitionRef = useRef(null);
  const prevMsgCountRef = useRef(0);
  useEffect(() => {
    // The thread on screen, captured as the effect runs and resolved the way the chat panel files
    // its lines (nothing selected is the draft; a branch's thread is its root's).
    const resolved = resolveChatTarget(runState, captureChatTarget(runState));
    const conv = resolved ? (runState.conversationsById?.[resolved.convId] ?? null) : null;
    const entries = conv?.entries ?? [];
    if (voiceMode && autoRead && entries.length > prevMsgCountRef.current) {
      const last = entries[entries.length - 1];
      if (last && last.role === "assistant" && last.content) {
        window.speechSynthesis.cancel();
        const utt = new SpeechSynthesisUtterance(last.content);
        utt.rate = 1.0;
        utt.onend = () => setSpeaking(false);
        utt.onerror = () => setSpeaking(false);
        setSpeaking(true); // eslint-disable-line react-hooks/set-state-in-effect -- BASE's idiom, unmasked by the move out of AppInner (where the compiler does not analyze): `speaking` is raised the moment the utterance is handed to speechSynthesis and cleared by its onend / onerror.
        window.speechSynthesis.speak(utt);
      }
    }
    prevMsgCountRef.current = entries.length;
  }, [runState, voiceMode, autoRead]);
  return {
    alternateSpaceDefaultPref,
    setAlternateSpaceDefaultPref,
    alternateSpaceDefault,
    applyAlternateSpace,
    debugUI,
    setDebugUI,
    responseCaptureOptIn,
    setResponseCaptureOptIn,
    settingsTitleClickRef,
    proOpen,
    setProOpen,
    accountOpen,
    setAccountOpen,
    proColors,
    preprocessVariantAvailable,
    preprocessVariantId,
    setPreprocessVariantId,
    preprocessVariants,
    preprocessVariantDeploymentDefault,
    preprocessVariantsLoading,
    glowSpine,
    setGlowSpine,
    glowControls,
    setGlowControls,
    compareEngine,
    setCompareEngine,
    forecastEngine,
    setForecastEngine,
    mirabilisLanes,
    setMirabilisLanes,
    orchestrators,
    setOrchestrators,
    triggerLlm,
    setTriggerLlm,
    compareTriggerLlm,
    setCompareTriggerLlm,
    healthError,
    retryHealth,
    theme,
    setTheme,
    uiScaleSetting,
    setUiScale,
    uiScale,
    isPhone,
    uiZoom,
    themeRaw,
    voiceMode,
    autoRead,
    setAutoRead,
    listening,
    setListening,
    speaking,
    setSpeaking,
    recognitionRef,
  };
}
